/**
 * Central navigation configuration for the Anike platform sidebar.
 *
 * This is the SINGLE SOURCE OF TRUTH for what appears in the main
 * navigation - no component outside of `useNavigation.js`/`AsideMenuList.js`
 * should decide what nav items exist. To add/change/remove a nav item,
 * edit this file only.
 *
 * Every existing ROUTE below is unchanged from before this redesign - only
 * the grouping/labels/icons and a handful of previously-unlinked
 * (but already-existing) pages have been added. See repo notes for the
 * full route/permission audit this was built from.
 *
 * Item shape:
 *   id           - unique string key (used for React keys, not shown to users)
 *   label        - visible menu text
 *   route        - the path this item links to (leaf items only)
 *   icon         - font-awesome/flaticon class string (e.g. "fas fa-cog")
 *   svgIcon      - path (via toAbsoluteUrl) to an SVG icon - used instead of `icon`
 *   permission   - a permission string, or an array of strings (ANY match = visible)
 *   superOnly    - true if only Super Admin platform accounts should see this
 *   entitlement  - optional future module-entitlement key (see
 *                  `hasModuleEntitlement` on the backend) - reserved for when
 *                  plan-based feature gating is actually turned on; every
 *                  item currently omits this since no plan restricts
 *                  anything yet (see companyContext.js's Plan seam notes)
 *   children     - nested items (max one level deep, for touch-friendliness -
 *                  see the mobile requirement in the nav redesign brief)
 *
 * A parent item with `children` is visible if ANY child is visible - it does
 * NOT need its own separate `permission`, avoiding the old "outer permission
 * check silently hides an inner item the user actually has permission for"
 * bug class that existed in the previous hand-written sidebar (e.g. the old
 * "Manage Asset" submenu required `manageAsset` on the wrapper even though
 * its own children were individually gated on the lower `viewAsset`
 * permission - a user with only `viewAsset` never saw the section at all).
 */

export const NAV_SECTIONS = [
  {
    id: "home",
    label: null, // no section header for the very first group
    items: [
      { id: "dashboard", label: "Dashboard", route: "/dashboard", svgIcon: "/media/svg/icons/Design/Layers.svg" },
      // Role-aware aggregation of the current user's own actionable items
      // (src/controller/myWork.js) - always visible, like Dashboard; the
      // backend itself permission-gates every section, so a user with no
      // applicable work simply sees the "all caught up" empty state.
      { id: "my-work", label: "My Work", route: "/my-work", icon: "flaticon2-list" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        id: "assets",
        label: "Assets",
        icon: "flaticon2-box",
        children: [
          { id: "assets-view", label: "View Assets", route: "/asset/asset-list", permission: "viewAsset" },
          // The full "Assigned Asset/Cluster" list (src/controller/asset.js's
          // GET /assigned) - distinct from "My Work" (a curated feed of
          // items needing action): this shows EVERY asset/site currently
          // assigned to the caller, regardless of whether anything on it
          // needs attention right now. Had no nav entry at all after the
          // earlier nav redesign repointed "My Work" away from this route -
          // a real, fully-working page was completely unreachable.
          { id: "assets-assigned", label: "Assigned to Me", route: "/asset/assigned", permission: "viewAssignedAsset" },
          { id: "assets-audit", label: "Asset Audit", route: "/asset/audit", permission: "viewAsset" },
          { id: "assets-categories", label: "Categories", route: "/category/view", permission: "manageCategory" },
          { id: "assets-verification", label: "Verification Campaigns", route: "/verification/campaigns", permission: "viewVerificationCampaign" },
        ],
      },
      { id: "sites-clusters", label: "Sites / Clusters", route: "/asset/cluster", icon: "flaticon2-placeholder", permission: "viewAsset" },
      {
        id: "service-requests",
        label: "Service Requests",
        icon: "fas fa-hand-holding-water",
        children: [
          { id: "sr-outgoing", label: "Outgoing", route: "/service-request/outgoing", permission: "viewOutgoingSR" },
          { id: "sr-incoming", label: "Incoming", route: "/service-request/incoming", permission: "viewIncomingSR" },
          { id: "sr-completed", label: "Completed", route: "/service-request/completed", permission: "viewCompletedSR" },
          // flattened from a 3-level-deep "Observation > Sent" submenu
          { id: "sr-observations-sent", label: "Sent Observations", route: "/service-request/observation/sent", permission: "viewSentObservation" },
        ],
      },
      {
        id: "work-orders",
        label: "Work Orders",
        icon: "fas fa-file-contract",
        children: [
          { id: "wo-approvals", label: "Approval Requests", route: "/work-order/approval-requests", permission: "approveWO" },
          { id: "wo-outgoing", label: "Outgoing", route: "/work-order/outgoing", permission: "viewOutgoingWO" },
          { id: "wo-incoming", label: "Incoming", route: "/work-order/incoming", permission: "viewIncomingWO" },
          { id: "wo-completed", label: "Completed", route: "/work-order/completed", permission: "viewCompletedWO" },
          { id: "wo-certified", label: "Certified", route: "/work-order/certified", permission: "viewCompletedWO" },
        ],
      },
      {
        id: "faults",
        label: "Faults",
        icon: "fas fa-book-dead",
        children: [
          { id: "faults-reported", label: "Reported Faults", route: "/asset/reported-faults", permission: ["manageAssetFault", "viewAsset", "viewAssignedAsset"] },
          { id: "faults-portal", label: "Fault Portal", route: "/asset/report-fault", permission: "manageAssetFault" },
        ],
      },
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    items: [
      { id: "warehouses", label: "Warehouses", route: "/manage-warehouse/view", icon: "fas fa-house-damage", permission: "viewWarehouse" },
      {
        id: "materials",
        label: "Materials",
        icon: "flaticon2-box",
        children: [
          // Both of these pages already existed but had NO sidebar entry at
          // all before this redesign (confirmed dead links in the nav audit).
          { id: "materials-all", label: "All Materials", route: "/manage-warehouse/total/material", permission: "viewWarehouse" },
          { id: "materials-low-stock", label: "Low Stock", route: "/manage-warehouse/below/threashold", permission: "viewWarehouse" },
          { id: "materials-requests", label: "Material Requests", route: "/material-request/list", permission: ["approveMaterialRequest", "stockInventory", "requestForMaterial"] },
        ],
      },
      {
        id: "purchase-orders",
        label: "Purchase Orders",
        icon: "fas fa-cart-plus",
        children: [
          { id: "po-outgoing", label: "Outgoing", route: "/purchase-order/outgoing", permission: "viewOutgoingPO" },
          { id: "po-incoming", label: "Incoming", route: "/purchase-order/incoming", permission: "viewIncomingPO" },
          { id: "po-completed", label: "Completed", route: "/purchase-order/completed", permission: "viewCompletedPO" },
          { id: "po-approvals", label: "Approval Requests", route: "/purchase-order/approval", permission: "approvePO" },
          { id: "po-supply", label: "Supply Confirmation", route: "/purchase-order/confirmation/supply", permission: "confirmSupply" },
        ],
      },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    items: [
      {
        id: "reports",
        label: "Reports",
        icon: "fas fa-chart-pie",
        children: [
          { id: "reports-overview", label: "Overview", route: "/report/overview", permission: ["viewAllSRReport", "viewAssetReport"] },
          { id: "reports-sr-wo", label: "SR & WO Report", route: "/report/sr-wo-report", permission: "viewAllSRReport" },
          { id: "reports-warehouse", label: "Warehouse Report", route: "/report/warehouse", permission: "viewWarehouseReport" },
          { id: "reports-scorecard", label: "Score Card", route: "/report/score-card", permission: "viewScoreCard" },
          { id: "reports-my-scorecard", label: "My Score Card", route: "/report/my-score-card", permission: "viewOwnScoreCard" },
          { id: "reports-financial", label: "Financial", route: "/report/financial", permission: "manageReport" },
          { id: "reports-maintenance-analytics", label: "Maintenance Analytics", route: "/report/maintenance-analytics", permission: "viewMaintenancePlan" },
          { id: "reports-verification-analytics", label: "Verification Analytics", route: "/report/verification-analytics", permission: "viewVerificationCampaign" },
          { id: "reports-coverage-analytics", label: "Coverage Analytics", route: "/report/coverage-analytics", permission: "viewAssetCoverage" },
          { id: "reports-advanced", label: "Advanced Reports", route: "/report/advanced-reports", permission: "viewAssetReport" },
        ],
      },
      // Promoted out of the Reports submenu (was previously also linked as
      // "/report/asset" inside it) so it's not a duplicate entry to the same
      // route under two labels - Asset Health is exactly what that report shows.
      { id: "asset-health", label: "Asset Health", route: "/report/asset", icon: "flaticon2-pie-chart", permission: "viewAssetReport" },
      // "Provider Performance" is intentionally NOT a separate nav item -
      // it is currently a section embedded inside the Overview report
      // (/report/overview), not its own page. Adding a second top-level
      // link to that same route would be a duplicate nav entry, which the
      // audit explicitly asked to avoid. Flagged in repo notes as a real
      // gap if a dedicated Provider Performance page is ever built.
    ],
  },
  {
    id: "administration",
    label: "Administration",
    items: [
      // Platform-level, cross-organisation - distinct from "Organisation"
      // below (which is scoped to the caller's own org only).
      {
        id: "platform-insights",
        label: "Platform Insights",
        icon: "flaticon2-line-chart",
        superOnly: true,
        children: [
          { id: "platform-overview", label: "Overview", route: "/platform-insights/overview" },
          { id: "platform-organisations", label: "Organisations", route: "/platform-insights/organisations" },
          { id: "platform-asset-analytics", label: "Asset Analytics", route: "/platform-insights/asset-analytics" },
          { id: "platform-operations-analytics", label: "Operations Analytics", route: "/platform-insights/operations-analytics" },
          { id: "platform-maintenance-analytics", label: "Maintenance Analytics", route: "/platform-insights/maintenance-analytics" },
          { id: "platform-verification-analytics", label: "Verification Analytics", route: "/platform-insights/verification-analytics" },
          { id: "platform-coverage-analytics", label: "Coverage Analytics", route: "/platform-insights/coverage-analytics" },
          { id: "platform-security-events", label: "Security Events", route: "/platform-insights/security-events" },
        ],
      },
      { id: "manage-companies", label: "Manage Companies", route: "/company/manage", icon: "far fa-building", superOnly: true },
      {
        id: "data-management",
        label: "Data Management",
        icon: "far fa-flask",
        superOnly: true,
        children: [
          { id: "data-management-seed-data", label: "Seed Data", route: "/data-management/seed-data" },
          { id: "data-management-backfills", label: "Data Backfills", route: "/data-management/backfills" },
          { id: "data-management-integrity", label: "Data Integrity", route: "/data-management/integrity" },
        ],
      },
      { id: "organisation", label: "Organisation", route: "/organisation/general", icon: "far fa-building", permission: "manageOrganisation" },
      {
        id: "users",
        label: "Users",
        icon: "far fa-user",
        children: [
          { id: "users-add", label: "Add User", route: "/user/add", permission: "createUser" },
          { id: "users-view", label: "View Users", route: "/user/view", permission: "manageUser" },
          { id: "users-requests", label: "Registration Requests", route: "/user/request", permission: "manageUserRegistration" },
          { id: "users-service-providers", label: "Manage Service Providers", route: "/user/service-providers", permission: "createUser" },
        ],
      },
      {
        id: "roles",
        label: "Roles",
        icon: "flaticon2-shield",
        children: [
          { id: "roles-create", label: "Create Role", route: "/user/roles/create", permission: "viewUserRoles" },
          { id: "roles-view", label: "User Roles", route: "/user/role", permission: "viewUserRoles" },
        ],
      },
      {
        id: "invitations",
        label: "Invitations",
        icon: "flaticon2-send",
        children: [
          { id: "invitations-pending", label: "Pending Invitations", route: "/organisation/invitations", permission: "createUser" },
          { id: "invitations-send", label: "Send Invite", route: "/user/invite", permission: "createUser" },
        ],
      },
      // Consolidates SR/WO/PO approval-chain configuration, previously
      // scattered as a "Configuration" sub-item buried inside the Work
      // Order and Purchase Order operational submenus - same routes, moved
      // here since configuring approval chains is an admin task, not a
      // day-to-day operational one.
      {
        id: "approval-workflows",
        label: "Approval Workflows",
        icon: "flaticon2-settings",
        children: [
          { id: "approval-wo", label: "Work Order Approvals", route: "/work-order/configuration", permission: "woConfiguration" },
          { id: "approval-po", label: "Purchase Order Approvals", route: "/purchase-order/configuration", permission: "poConfiguration" },
        ],
      },
      { id: "slas", label: "SLAs", route: "/sla/manage", icon: "fab fa-wpforms", permission: "manageAsset" },
      { id: "pricebook", label: "Pricebook", route: "/pricebook/view", icon: "fas fa-book", permission: "managePricebook" },
      // "Notifications" (admin-configurable notification settings) is
      // intentionally omitted - no such settings page exists yet (only the
      // in-app notification bell, which isn't a navigable module). Adding a
      // nav item here would be fake navigation to nothing. Flagged in repo
      // notes as a real gap for a future task.
    ],
  },
];

/**
 * Nav experience for accounts still awaiting approval (see
 * src/middleware/onboarding.js's ACCOUNT_STATUS on the backend). Deliberately
 * tiny - only sections that correspond to something the account can ACTUALLY
 * do right now. "Users"/team-management is NOT included here: a pending
 * account has no `userRole` at all until approval (role assignment happens
 * at approval time - see user.js's approve-registration endpoint), so every
 * user/invite-management endpoint is unreachable for them regardless of
 * what the nav shows - including a nav item for it would be fake navigation.
 */
export const PENDING_ACCOUNT_NAV_ITEMS = [
  { id: "account-status", label: "Status" },
  { id: "account-organisation", label: "Organisation" }, // only shown for organisation-type onboarding, see useNavigation/AccountWorkspace
  { id: "account-profile", label: "Profile" },
];
